import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signIn } from 'aws-amplify/auth';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from './useAuth.js';
import { AuthWordmark } from './AuthWordmark.js';
import { describeAuthError } from './auth-errors.js';
import './auth.css';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage(): JSX.Element {
  const { refresh, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Navigate only after the auth context has committed the new state.
  // Calling navigate() directly after refresh() races with React's batched state update.
  useEffect(() => {
    if (isAuthenticated) {
      void navigate({ to: '/dashboard', replace: true });
    }
  }, [isAuthenticated, navigate]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await signIn({ username: values.email, password: values.password });
      await refresh(); // triggers setUser → isAuthenticated → useEffect above navigates
    } catch (err) {
      setSubmitError(describeAuthError(err));
    }
  });

  return (
    <div className="auth-page">
      <div className="auth-card" role="form" aria-labelledby="login-title">
        <AuthWordmark />
        <h1 id="login-title" className="auth-title">
          Sign in
        </h1>
        <p className="auth-subtitle">Welcome back. Pick up where you left off.</p>

        {submitError && (
          <div className="auth-banner-error" role="alert">
            {submitError}
          </div>
        )}

        <form onSubmit={onSubmit} noValidate>
          <label className="auth-field">
            <span className="auth-label">Email</span>
            <input
              type="email"
              autoComplete="email"
              autoFocus
              className="auth-input"
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email')}
            />
            {errors.email && (
              <span className="auth-error-field" role="alert">
                {errors.email.message}
              </span>
            )}
          </label>

          <label className="auth-field">
            <span className="auth-label">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              className="auth-input"
              aria-invalid={errors.password ? 'true' : 'false'}
              {...register('password')}
            />
            {errors.password && (
              <span className="auth-error-field" role="alert">
                {errors.password.message}
              </span>
            )}
          </label>

          <button type="submit" className="auth-button" disabled={isSubmitting}>
            {isSubmitting && <span className="auth-button-spinner" aria-hidden />}
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="auth-link-row">
          <Link className="auth-link" to="/forgot-password">
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}
